import { useState, useCallback } from 'react';
import { db } from '../lib/supabase';
import { log } from '../utils/logger';

/**
 * Custom hook for managing hotel search and booking functionality
 * Handles hotel search UI, data preservation, and confirmation states
 * @returns {Object} Hotel management state and handlers
 */
export const useHotelManagement = () => {
  const [showHotelSearch, setShowHotelSearch] = useState(false);
  const [hotelSearchData, setHotelSearchData] = useState(null);
  const [hotelConfirmation, setHotelConfirmation] = useState(null);
  const [preservedReservationState, setPreservedReservationState] = useState(null);

  // Hotel search handler
  const handleHotelSearch = useCallback(async (tournament, team, source = null, reservationContext = null) => {
    log.info('🏨 Hotel search handler called:', { 
      tournament: tournament?.name, 
      tournamentId: tournament?.id,
      team: team?.name,
      teamId: team?.id,
      source,
      reservationContext 
    });
    
    // Preserve reservation context if coming from reservations
    if (source === 'reservations' && reservationContext) {
      log.info('🏨 Preserving reservation context:', reservationContext);
      setPreservedReservationState(reservationContext);
    }
    
    try {
      // Check if there's already an existing hotel for this team/tournament
      log.info('🏨 Calling getTeamTournamentHotels with:', { teamId: team.id, tournamentId: tournament.id });
      const { data: existingHotels, error } = await db.getTeamTournamentHotels(team.id, tournament.id);
      
      log.info('🏨 Database response:', { 
        existingHotels, 
        error, 
        hotelsLength: existingHotels?.length 
      });
      
      let existingHotel = null;
      if (!error && existingHotels && existingHotels.length > 0) {
        existingHotel = existingHotels[0]; // Get the first (and should be only) hotel
        log.info('🏨 Found existing hotel:', existingHotel.hotel_name, 'ID:', existingHotel.id);
      } else {
        log.info('🏨 No existing hotel found, will create new one');
        if (error) {
          log.error('🏨 Database error:', error);
        }
      }
      
      log.info('🏨 Setting hotel search data:', { tournament, team, existingHotel, source });
      setHotelSearchData({ tournament, team, existingHotel, source });
      setShowHotelSearch(true);
    } catch (error) {
      log.error('Error checking for existing hotel:', error);
      setHotelSearchData({ tournament, team, existingHotel: null, source });
      setShowHotelSearch(true);
    }
  }, []);

  // Handle hotel selection and save
  const handleHotelSelect = useCallback(async (hotelData) => {
    log.info('🏨 Hotel selected, saving to database:', hotelData);
    
    try {
      // Prepare the hotel data for database insertion/update
      const hotelRecord = {
        team_id: hotelSearchData.team.id,
        tournament_id: hotelSearchData.tournament.id,
        hotel_name: hotelData.hotel_name,
        hotel_address: hotelData.hotel_address,
        latitude: hotelData.latitude,
        longitude: hotelData.longitude,
        place_id: hotelData.place_id,
        created_by: hotelData.created_by
      };
      
      log.info('🏨 Saving hotel record:', hotelRecord);
      log.info('🏨 Current hotelSearchData:', hotelSearchData);
      log.info('🏨 existingHotel check:', {
        existingHotel: hotelSearchData.existingHotel,
        hasExistingHotel: !!hotelSearchData.existingHotel,
        existingHotelId: hotelSearchData.existingHotel?.id
      });
      
      let result;
      // Check if we're updating an existing hotel or creating a new one
      if (hotelSearchData.existingHotel) {
        log.info('🏨 Updating existing hotel with ID:', hotelSearchData.existingHotel.id);
        result = await db.updateTeamTournamentHotel(hotelSearchData.existingHotel.id, hotelRecord);
      } else {
        log.info('🏨 Creating new hotel record');
        result = await db.createTeamTournamentHotel(hotelRecord);
      }
      
      if (result.error) throw result.error;
      
      log.info('🏨 Hotel saved successfully:', result);
      
      // Trigger a refresh of tournament data to update hotel status
      if (hotelSearchData?.team?.id) {
        log.info('🔄 Refreshing tournament data after hotel save...');
        window.dispatchEvent(new CustomEvent('hotelSaved', { 
          detail: { 
            teamId: hotelSearchData.team.id, 
            tournamentId: hotelSearchData.tournament.id 
          } 
        }));
      }
      
      // Show confirmation instead of alert
      setShowHotelSearch(false);
      setHotelSearchData(null);
      setHotelConfirmation({
        success: true,
        message: 'Team accommodation set successfully!',
        teamName: hotelSearchData.team.name,
        tournamentName: hotelSearchData.tournament.name,
        hotelName: hotelData.hotel_name
      });
      
    } catch (err) {
      // Error caught and ignored
      log.error('Error saving hotel:', err);
      setShowHotelSearch(false);
      setHotelSearchData(null);
      setHotelConfirmation({
        success: false,
        message: 'Failed to set team accommodation. Please try again.',
        teamName: hotelSearchData?.team?.name || 'Unknown Team',
        tournamentName: hotelSearchData?.tournament?.name || 'Unknown Tournament'
      });
    }
  }, [hotelSearchData]);

  // Handle hotel search cancel
  const handleHotelCancel = useCallback(() => {
    log.info('🏨 Cancel button clicked from hotel search, source:', hotelSearchData?.source);
    
    // If returning from reservations, restore the preserved state
    if (hotelSearchData?.source === 'reservations' && preservedReservationState) {
      log.info('🏨 Restoring preserved reservation state after cancel:', preservedReservationState);
      // Return information about what should be restored
      const restoreInfo = { shouldRestoreReservations: true, preservedState: preservedReservationState };
      setShowHotelSearch(false);
      setHotelSearchData(null);
      return restoreInfo;
    }
    
    setShowHotelSearch(false);
    setHotelSearchData(null);
    return { shouldRestoreReservations: false };
  }, [hotelSearchData, preservedReservationState]);

  // Handle hotel search back
  const handleHotelBack = useCallback(() => {
    log.info('🏨 Back button clicked from hotel search, source:', hotelSearchData?.source);
    log.info('🏨 Current preserved state:', preservedReservationState);
    
    // If returning from reservations, keep the preserved state
    if (hotelSearchData?.source === 'reservations') {
      if (preservedReservationState) {
        log.info('🏨 Maintaining preserved reservation state for return:', preservedReservationState);
        // Don't clear the state - let it persist until user navigates away
      } else {
        log.info('🏨 WARNING: No preserved state found when returning from hotel search!');
      }
      const restoreInfo = { shouldRestoreReservations: true, preservedState: preservedReservationState };
      setShowHotelSearch(false);
      setHotelSearchData(null);
      return restoreInfo;
    }
    
    setShowHotelSearch(false);
    setHotelSearchData(null);
    return { shouldRestoreReservations: false };
  }, [hotelSearchData, preservedReservationState]);

  // Clear hotel confirmation
  const clearHotelConfirmation = useCallback(() => {
    setHotelConfirmation(null);
  }, []);

  // Clear preserved reservation state
  const clearPreservedReservationState = useCallback(() => {
    setPreservedReservationState(null);
  }, []);

  return {
    // State
    showHotelSearch,
    hotelSearchData,
    hotelConfirmation,
    preservedReservationState,
    
    // Actions
    handleHotelSearch,
    handleHotelSelect,
    handleHotelCancel,
    handleHotelBack,
    clearHotelConfirmation,
    setPreservedReservationState,
    clearPreservedReservationState,
    
    // Direct state setters for special cases
    setShowHotelSearch,
    setHotelSearchData,
    setHotelConfirmation
  };
};